import { useEffect, useState } from "react";
import { api } from "@/client/lib/api";
import { AdminShell } from "@/client/components/admin/AdminShell";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { DataTable, type DataTableColumn } from "@/client/components/admin/DataTable";
import {
  ActivityIcon,
  AlertCircleIcon,
  InfoIcon,
  SearchIcon,
  SendIcon,
  Undo2Icon,
  LogInIcon,
  MailIcon,
  PlayIcon,
  CircleDashedIcon,
  CheckCircle2Icon,
  CircleCheckBigIcon,
  PencilIcon,
} from "lucide-react";

type Stream = "activity" | "audit";

type LogEvent = {
  id: string;
  ts: string;
  kind: string;
  actor: string | null;
  actorEmail: string | null;
  title: string;
  detail: string | null;
};

const KIND_ICON: Record<string, typeof SendIcon> = {
  login: LogInIcon,
  login_link: MailIcon,
  aanlevering: SendIcon,
  revert: Undo2Icon,
  started: PlayIcon,
  section_started: CircleDashedIcon,
  section_complete: CheckCircle2Icon,
  flow_ready: CircleCheckBigIcon,
  mutation: PencilIcon,
};
const KIND_TINT: Record<string, string> = {
  login: "bg-blue-50 text-blue-600",
  login_link: "bg-blue-50/70 text-blue-500",
  aanlevering: "bg-emerald-50 text-emerald-600",
  revert: "bg-amber-50 text-amber-600",
  started: "bg-sky-50 text-sky-600",
  section_started: "bg-muted text-muted-foreground",
  section_complete: "bg-emerald-50 text-emerald-600",
  flow_ready: "bg-emerald-100 text-emerald-700",
  mutation: "bg-violet-50 text-violet-600",
};
const KIND_LABEL: Record<string, string> = {
  login: "Inloggen",
  login_link: "Inloglink",
  aanlevering: "Aanlevering",
  revert: "Teruggezet",
  started: "Gestart",
  section_started: "Onderdeel gestart",
  section_complete: "Onderdeel klaar",
  flow_ready: "Klaar",
  mutation: "Wijziging",
};

// Bound the cursor drain: the server tails ~1 MiB per log file, so a few thousand rows is the
// ceiling. 200/page * 15 = 3000 rows is comfortably above that while preventing a runaway loop.
const PAGE_LIMIT = 200;
const SAFETY_MAX_FETCHES = 15;

// Dutch date + time in Europe/Amsterdam. Same-day events show only the time so the column stays
// compact; older events show a short date. The full datetime is in the cell's title tooltip.
function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const now = new Date();
    const sameDay =
      d.toLocaleDateString("nl-NL", { timeZone: "Europe/Amsterdam" }) ===
      now.toLocaleDateString("nl-NL", { timeZone: "Europe/Amsterdam" });
    const time = d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" });
    if (sameDay) return `vandaag ${time}`;
    const date = d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", timeZone: "Europe/Amsterdam" });
    return `${date} ${time}`;
  } catch {
    return iso;
  }
}

function formatFull(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "Europe/Amsterdam",
    });
  } catch {
    return iso;
  }
}

function TypeBadge({ kind }: { kind: string }) {
  const Icon = KIND_ICON[kind] ?? ActivityIcon;
  const tint = KIND_TINT[kind] ?? "bg-muted text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${tint}`}>
      <Icon size={12} />
      {KIND_LABEL[kind] ?? kind}
    </span>
  );
}

// Audit rows fold the HTTP status into `detail` ("... mislukt (409)"); a failed write is the only
// case where that phrase appears, so it cleanly drives an OK/Mislukt badge.
function ResultBadge({ detail }: { detail: string | null }) {
  const failed = !!detail && /mislukt/i.test(detail);
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
        failed ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"
      }`}
    >
      {failed ? "Mislukt" : "OK"}
    </span>
  );
}

function WhatCell({ e }: { e: LogEvent }) {
  return (
    <span>
      <span className="text-foreground">{e.title}</span>
      {e.detail && <span className="text-muted-foreground"> · {e.detail}</span>}
    </span>
  );
}

// Name with the e-mail below it, so two people with the same name are still distinguishable. The
// e-mail is only present for person actors (logins, staff mutations); school-level rows show just
// the school name.
function ActorCell({ actor, email }: { actor: string | null; email: string | null }) {
  if (!actor) return <span className="text-muted-foreground/50">—</span>;
  return (
    <span className="block max-w-[16rem]">
      <span className="block truncate text-foreground" title={actor}>
        {actor}
      </span>
      {email && (
        <span className="block truncate text-xs text-muted-foreground" title={email}>
          {email}
        </span>
      )}
    </span>
  );
}

const TIME_COLUMN: DataTableColumn<LogEvent> = {
  key: "ts",
  header: "Tijd",
  sortable: true,
  sortValue: (e) => e.ts,
  cellClassName: "whitespace-nowrap tabular-nums text-muted-foreground",
  cell: (e) => <span title={formatFull(e.ts)}>{formatTime(e.ts)}</span>,
};

const ACTIVITY_COLUMNS: DataTableColumn<LogEvent>[] = [
  TIME_COLUMN,
  { key: "kind", header: "Type", cell: (e) => <TypeBadge kind={e.kind} /> },
  { key: "actor", header: "Wie", cell: (e) => <ActorCell actor={e.actor} email={e.actorEmail} /> },
  { key: "what", header: "Wat", cellClassName: "w-full", cell: (e) => <WhatCell e={e} /> },
];

const AUDIT_COLUMNS: DataTableColumn<LogEvent>[] = [
  TIME_COLUMN,
  { key: "result", header: "Resultaat", cell: (e) => <ResultBadge detail={e.detail} /> },
  { key: "actor", header: "Wie", cell: (e) => <ActorCell actor={e.actor} email={e.actorEmail} /> },
  { key: "what", header: "Actie", cellClassName: "w-full", cell: (e) => <WhatCell e={e} /> },
];

function LogFeed({ stream }: { stream: Stream }) {
  const isAudit = stream === "audit";
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState(""); // debounced search term, sent to the server

  // Debounce the search box so we re-query at most a few times per typing burst.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Drain the whole matching window (bounded) so we can paginate client-side with real page
  // numbers. The search term goes to the SERVER (q) so matches in older entries still surface,
  // not only within the already-loaded rows.
  async function load(q: string) {
    setError(false);
    try {
      const all: LogEvent[] = [];
      let before: string | null = null;
      let avail = true;
      for (let i = 0; i < SAFETY_MAX_FETCHES; i++) {
        const params: Record<string, string> = { stream, limit: String(PAGE_LIMIT) };
        if (q) params.q = q;
        if (before) params.before = before;
        const res = await (api.api.admin as any).logs.$get({ query: params });
        if (!res.ok) {
          setError(true);
          return;
        }
        const data = await res.json();
        avail = data.available !== false;
        const incoming: LogEvent[] = Array.isArray(data.events) ? data.events : [];
        all.push(...incoming);
        if (!data.nextCursor) break;
        before = data.nextCursor;
      }
      setAvailable(avail);
      setEvents(all);
    } catch {
      setError(true);
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      await load(query);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, query]);

  return (
    <AdminShell>
      <AdminPageHeader
        items={[{ label: isAudit ? "Audit" : "Activiteit" }]}
        subtitle={
          isAudit
            ? "Elke wijziging door het KUVO-team, met wie en wanneer. Voor verantwoording en herleidbaarheid."
            : "Wat de scholen doen: inloggen, voortgang en aanleveringen."
        }
      />

      {/* Search */}
      <div className="mb-4 max-w-sm">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={isAudit ? "Zoek op naam of actie..." : "Zoek op school, naam of actie..."}
          startIcon={<SearchIcon />}
        />
      </div>

      {loading ? (
        <div className="space-y-2 rounded-lg border p-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/5" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm">
          <p className="flex items-center gap-2 font-medium text-destructive">
            <AlertCircleIcon size={16} /> Kan dit niet laden
          </p>
          <p className="mt-1 text-muted-foreground">Probeer het over enkele ogenblikken opnieuw.</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => {
              setLoading(true);
              load(query).finally(() => setLoading(false));
            }}
          >
            Opnieuw proberen
          </Button>
        </div>
      ) : !available ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50/50 p-6 text-sm">
          <p className="flex items-center gap-2 font-medium text-amber-700">
            <InfoIcon size={16} /> Nog niets vastgelegd
          </p>
          <p className="mt-1 text-muted-foreground">
            {isAudit
              ? "Zodra het team iets wijzigt, verschijnt het hier."
              : "Zodra scholen inloggen of aanleveren, verschijnt het hier."}
          </p>
        </div>
      ) : (
        <DataTable
          columns={isAudit ? AUDIT_COLUMNS : ACTIVITY_COLUMNS}
          data={events}
          getRowKey={(e) => e.id}
          initialSort={{ key: "ts", dir: "desc" }}
          defaultPageSize={50}
          pageSizeOptions={[25, 50, 100]}
          resetKey={query}
          emptyMessage={
            query
              ? "Geen resultaten voor deze zoekopdracht."
              : isAudit
                ? "Nog geen wijzigingen."
                : "Nog geen activiteit."
          }
        />
      )}
    </AdminShell>
  );
}

export function ActiviteitPage() {
  return <LogFeed stream="activity" />;
}
export function AuditPage() {
  return <LogFeed stream="audit" />;
}
